import { TermLevel } from "./custom_view/TermLevel";
import i18n from "../../i18n";
import Sentence from "../sentence/model/Sentence";

export enum TermLevelUpdate {
  increase,
  reduce,
  none,
}

export default class Term {
  id: string;
  term: string;
  definition: string;
  memo: string;
  pronunciation: string;
  group: string;
  level: TermLevel;
  exp: number;
  isShowDetail: boolean = false;
  isMarked: boolean = false;
  selected: boolean = false;
  expMovePoint: number = 0;
  isCorrectAnswer: boolean;
  updatedLevel: TermLevelUpdate;
  isGeneratingSentence: boolean = false;
  isMoreSentenceLoading: boolean = false;
  index?: number;
  defaultHints?: string;
  learningCount?: number;
  sentence?: Sentence;

  constructor(
    id: string,
    term: string,
    definition: string,
    memo: string,
    pronunciation: string,
    group: string,
    level: TermLevel,
    exp: number,
    expMovePoint: number,
    isMarked: boolean,
    isCorrectAnswer: boolean | number,
    updateLevel: TermLevelUpdate,
    index?: number,
    learningCount?: number,
    defaultHints?: string
  ) {
    this.id = id;
    this.term = term;
    this.definition = definition;
    this.memo = memo;
    this.pronunciation = pronunciation;
    this.group = group;
    this.level = level;
    this.exp = exp;
    this.expMovePoint = expMovePoint;
    this.isMarked = isMarked;
    this.isCorrectAnswer = Boolean(isCorrectAnswer);
    this.updatedLevel = updateLevel;
    this.index = index;
    this.learningCount = learningCount;
    this.defaultHints = defaultHints;
  }

  static makeSimpleTerm(
    term: string,
    definition: string,
    pronunciation: string,
    memo: string
  ): Term {
    return new Term(
      "",
      term,
      definition,
      memo,
      pronunciation,
      "",
      TermLevel.difficult,
      0,
      0,
      false,
      false,
      TermLevelUpdate.none
    );
  }

  static toJson(term: Term): any {
    return {
      id: term.id,
      term: term.term,
      definition: term.definition,
      memo: term.memo,
      pronunciation: term.pronunciation,
      group: term.group,
      level: term.level,
      exp: term.exp,
      isMarked: term.isMarked,
      isCorrectAnswer: term.isCorrectAnswer,
      isShowDetail: term.isShowDetail,
      selected: term.selected,
      defaultHints: term.defaultHints,
    };
  }

  static fromJson(json: any): Term {
    let level = TermLevel.difficult;
    switch (json.lv ?? json.new_level ?? json.level) {
      case "0":
      case 0:
        level = TermLevel.difficult;
        break;
      case "1":
      case 1:
        level = TermLevel.familiar;
        break;
      case "2":
      case 2:
        level = TermLevel.perfect;
        break;
    }

    let updatedLevel = TermLevelUpdate.none;
    switch (json.change) {
      case 1:
        updatedLevel = TermLevelUpdate.increase;
        break;
      case -1:
        updatedLevel = TermLevelUpdate.reduce;
        break;
      case 0:
        updatedLevel = TermLevelUpdate.none;
        break;
    }

    return new Term(
      json.nid ?? json.id,
      json.wd ?? json.word ?? json.term,
      json.mn ?? json.mean ?? json.definition,
      json.de ?? json.desc ?? json.memo,
      json.pr ?? json.pron ?? json.pronunciation,
      json.gr ?? json.group,
      level,
      json.exp,
      json.move_point ?? json.expMovePoint,
      json.isMarked,
      Boolean(Number(json.true_and_false ?? json.isCorrectAnswer)),
      updatedLevel,
      json.index,
      json.learningCount,
      json.defaultHints
    );
  }
}

export function getUpdatedLevelString(updatedLevel: TermLevelUpdate): string {
  switch (updatedLevel) {
    case TermLevelUpdate.increase:
      return "Level up 🎉";
    case TermLevelUpdate.reduce:
      return "Level down 😥";
    case TermLevelUpdate.none:
      return "";
  }
}

export function getLevelString(level: TermLevel): string {
  switch (level) {
    case TermLevel.difficult:
      return i18n.t("Difficult");
    case TermLevel.familiar:
      return i18n.t("Familiar");
    case TermLevel.perfect:
      return i18n.t("Perfect");
  }
}

export function getGroupString(group: string): string {
  switch (group) {
    case "":
      return i18n.t("NoSelectGroup");
    default:
      return group;
  }
}

export function getNextLevel(level: TermLevel): TermLevel {
  switch (level) {
    case TermLevel.difficult:
      return TermLevel.familiar;
    case TermLevel.familiar:
      return TermLevel.perfect;
    case TermLevel.perfect:
      return TermLevel.difficult;
  }
}

export function isValidTerm(term: Term): boolean {
  const isValid =
    term.term.length !== 0 &&
    (term.definition.length !== 0 ||
      (term.defaultHints !== undefined && term.defaultHints.length !== 0));
  return isValid;
}

export const termMaxSize = 2000;
export const definitionMaxSize = 2000;
export const memoMaxSize = 2000;
export const pronunciationMaxSize = 2000;
